import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import type { Recurso, Zona } from '../../api/ops/pagos';
import type { Cierre, CierreNuevo, ReservaAfectada } from '../../api/ops/reservasGestion';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Fecha } from '../../componentes/Fecha';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Tabla } from '../../componentes/Tabla';
import { Ventana } from '../../componentes/Ventana';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { usePermisos } from '../../permisos/permisos';
import { todas, type ContextoReservas } from '../reservas/contexto';
import { diaCorto, horaEn, hoyEn } from '../reservas/fechas';
import { mayuscula } from '../reservas/textos';
import { instanteLocal } from '../sala/logica';

/**
 * 8.8 Cierre de recursos (apartado «Cierres» de Recursos, `?apartado=cierres`, `?cierre=` abre uno; D-071, C8, G400):
 * «En curso y próximos» · «Terminados» (`listResourceClosures`), la franja de los clientes por llamar y la tabla. Nuevo
 * cierre en dos pasos: qué (recursos sueltos o zonas enteras), cuándo, motivo y dónde recolocar → «Revisar el impacto»
 * (`previewResourceClosure`, válido 15 min) → «Confirmar el cierre» (`applyResourceClosure` con los cambios a la propuesta:
 * otro recurso o «llamar»). La ficha enseña los clientes a llamar (nombre y teléfono con `listCustomers` por `customer_id`,
 * G797) con «Llamar» (`tel:`), «Otra hora» (la ficha de la reserva, Cambiar) y «Cancelar y devolver» (`cancelReservation`,
 * devolución íntegra), «Recolocar en» (las zonas de `relocated_resource_ids` de cada reserva) y «Lo cerró»
 * (`applied_by_name` y `applied_at`, G883), y «Reabrir» (no devuelve lo recolocado, G400). Gestionan Propietario, Administrador y Encargado
 * (`reservas.cierres.gestionar`); los demás, solo ver.
 */
const PERMISO = 'reservas.cierres.gestionar';
const MOTIVO: Readonly<Record<string, string>> = { breakdown: 'Avería', private_event: 'Evento privado', weather: 'Mal tiempo', other: 'Otro motivo' };
const SALIDA: Readonly<Record<ReservaAfectada['outcome'], readonly [string, EstadoChip]>> = {
    proposed: ['Propuesta', 'neutral'],
    relocated: ['Avisada', 'confirmed'],
    needs_call: ['Por llamar', 'pending'],
    rescheduled: ['Otra hora', 'confirmed'],
    cancelled: ['Cancelada', 'neutral'],
};
const LLAMAR = 'llamar';

interface Props {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    soloLectura: boolean;
    contexto: ContextoReservas;
    /** Cada vez que cambia, se abre «Cerrar recursos» (la acción de la cabecera de Recursos). */
    nuevo: number;
    abrirCierre: string | null;
    navegar: (ruta: string) => void;
    onAbrir: (id: string | null) => void;
}

type Fase = 'activos' | 'terminados';

function estadoDe(c: Cierre, ahora: number): readonly [string, EstadoChip, Fase] {
    if (c.status === 'reopened') {
        return ['Reabierto', 'neutral', 'terminados'];
    }
    if (c.status !== 'applied') {
        return ['Sin aplicar', 'draft', 'terminados'];
    }
    if (Date.parse(c.ends_at) <= ahora) {
        return ['Terminado', 'neutral', 'terminados'];
    }
    return Date.parse(c.starts_at) <= ahora ? ['En curso', 'problem', 'activos'] : ['Programado', 'neutral', 'activos'];
}

/** «Hoy, 12:00 a vie 16 oct, 23:59» en la zona del negocio. */
function cuando(c: Pick<Cierre, 'starts_at' | 'ends_at'>, zona: string): string {
    const hoy = hoyEn(zona);
    const dia = (iso: string) => {
        const d = hoyEn(zona, new Date(iso));
        return d === hoy ? 'Hoy' : diaCorto(d);
    };
    const a = dia(c.starts_at);
    const b = dia(c.ends_at);
    return `${a}, ${horaEn(c.starts_at, zona)} a ${b === a ? '' : `${b}, `}${horaEn(c.ends_at, zona)}`;
}

/** «Reservado S11», «Terraza entera» o la lista de lo que se cierra. */
function queSeCierra(c: Pick<Cierre, 'targets'>, contexto: ContextoReservas): string {
    const nombres = c.targets.map((t) => {
        if (t.zone_id !== undefined) {
            return `${contexto.zonas.find((z) => z.id === t.zone_id)?.name ?? 'Zona'} entera`;
        }
        const r = contexto.recursos.find((x) => x.id === t.resource_id);
        return `${mayuscula(contexto.palabra)} ${r?.code ?? ''}`.trim();
    });
    return nombres.length <= 1 ? (nombres[0] ?? '') : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1] ?? ''}`;
}

function useClientes(ops: Operaciones, negocio: string, ids: readonly string[], puede: boolean) {
    const [datos, setDatos] = useState<Readonly<Record<string, { nombre: string; telefono: string | null }>>>({});
    const faltan = puede ? [...new Set(ids)].filter((x) => !(x in datos)).sort().join(',') : '';
    useEffect(() => {
        if (faltan === '') {
            return;
        }
        let vivo = true;
        const lista = faltan.split(',');
        ops.listCustomersByIds(negocio, lista)
            .then((l) => {
                if (vivo) {
                    setDatos((x) => ({ ...x, ...Object.fromEntries(lista.map((id) => {
                        const c = l.data.find((y) => y.id === id);
                        return [id, { nombre: c?.display_name ?? 'Cliente', telefono: c?.phone ?? null }];
                    })) }));
                }
            })
            .catch(() => { if (vivo) { setDatos((x) => ({ ...x, ...Object.fromEntries(lista.map((id) => [id, { nombre: 'Cliente', telefono: null }])) })); } });
        return () => { vivo = false; };
    }, [ops, negocio, faltan]);
    return datos;
}

export function Cierres({ ops, negocio, movil, soloLectura, contexto, nuevo, abrirCierre, navegar, onAbrir }: Props) {
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const gestiona = permisos.puede(PERMISO) && !soloLectura;
    const [lista, setLista] = useState<Cierre[] | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [fase, setFase] = useState<Fase>('activos');
    // «Cerrar recursos» de la cabecera sube `nuevo`; el panel queda abierto hasta que se cierra con ese valor.
    const [cerradoEn, setCerradoEn] = useState(0);
    const creando = gestiona && nuevo > cerradoEn;
    const recargar = useCallback(() => { setIntento((i) => i + 1); }, []);
    const [ahora] = useState(() => Date.now());

    useEffect(() => {
        let vivo = true;
        todas((cursor) => ops.listResourceClosures(negocio, { cursor, limit: 100 }))
            .then((l) => { if (vivo) { setLista(l.filter((c) => c.status === 'applied' || c.status === 'reopened')); setError(null); } })
            .catch((e: unknown) => { if (vivo) { setError(e); } });
        return () => { vivo = false; };
    }, [ops, negocio, intento]);

    const ids = useMemo(() => (lista ?? []).flatMap((c) => c.affected.map((a) => a.customer_id ?? '')).filter((x) => x !== ''), [lista]);
    const clientes = useClientes(ops, negocio, ids, permisos.puede('clientes.fichas.ver'));

    if (error !== null && lista === null) {
        const e = estadoDeError(error);
        return e.tipo === 'sin-permiso' ? <EstadoSinPermiso seccion="Cierres" rol={contexto.rol} negocio={contexto.nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} /> : <EstadoError seccion="Cierres" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (lista === null) {
        return <EstadoCargando seccion="Cierres" />;
    }

    const deFase = lista.filter((c) => estadoDe(c, ahora)[2] === fase).sort((a, b) => (fase === 'activos' ? a.starts_at.localeCompare(b.starts_at) : b.starts_at.localeCompare(a.starts_at)));
    const porLlamar = lista.filter((c) => estadoDe(c, ahora)[2] === 'activos').flatMap((c) => c.affected.filter((a) => a.outcome === 'needs_call').map(() => c));
    const primero = porLlamar[0];
    const abierto = abrirCierre === null ? null : lista.find((c) => c.id === abrirCierre) ?? null;
    const FASES: readonly { valor: Fase; texto: string }[] = [{ valor: 'activos', texto: 'En curso y próximos' }, { valor: 'terminados', texto: 'Terminados' }];
    const idx = FASES.findIndex((f) => f.valor === fase);

    return (
        <>
            <div className="rv-sl-filtro">
                <button type="button" className="rv-sl-filtro__pastilla" aria-label={`Cierres: ${FASES[idx]?.texto ?? ''}, ${String(deFase.length)}`}>
                    <span>{FASES[idx]?.texto}</span>
                    <b>{deFase.length}</b>
                </button>
                <button type="button" className="rv-sl-redondo rv-sl-redondo--chico" aria-label="Ver los cierres siguientes" onClick={() => { setFase(fase === 'activos' ? 'terminados' : 'activos'); }}>
                    <Icono nombre="chevron-right" />
                </button>
            </div>
            {primero !== undefined && (
                <div className="rv-rc-aviso" role="status">
                    <span className="rv-rc-aviso__icono" aria-hidden="true"><Icono nombre="phone" /></span>
                    <span>
                        <strong>{porLlamar.length === 1 ? '1 cliente por llamar' : `${String(porLlamar.length)} clientes por llamar`}</strong>
                        <small>{`El cierre de ${queSeCierra(primero, contexto)} dejó reservas sin sitio. Llámales: otra hora o cancelar con devolución.`}</small>
                    </span>
                    <button type="button" className="rv-b rv-b-sec" onClick={() => { onAbrir(primero.id); }}><span>Ver la lista</span><Icono nombre="arrow-right" /></button>
                </div>
            )}
            <Tabla
                etiqueta="Cierres"
                movil={movil}
                columnas={[
                    { clave: 'que', titulo: 'Qué se cierra', ancho: 'minmax(0, 2.2fr)' },
                    { clave: 'cuando', titulo: 'Cuándo', ancho: 'minmax(0, 1.8fr)' },
                    { clave: 'reservas', titulo: 'Reservas' },
                    { clave: 'estado', titulo: 'Estado' },
                ]}
                vacia={<p className="rv-sl-nota rv-sl-nota--centro">{fase === 'activos' ? 'No hay nada cerrado ni ningún cierre programado.' : 'Aún no hay cierres terminados.'}</p>}
                filas={deFase.map((c) => {
                    const [texto, chip] = estadoDe(c, ahora);
                    const llamar = c.affected.filter((a) => a.outcome === 'needs_call').length;
                    const que = queSeCierra(c, contexto);
                    const motivo = [MOTIVO[c.reason_kind ?? ''] ?? c.reason_kind ?? '', c.reason ?? ''].filter((x) => x !== '').join(' • ');
                    return {
                        clave: c.id,
                        etiqueta: que,
                        onAbrir: () => { onAbrir(c.id); },
                        celdas: {
                            que: <span><strong>{que}</strong><br /><small>{motivo}</small></span>,
                            cuando: cuando(c, contexto.zona),
                            reservas: llamar > 0 ? <Chip estado="pending">{`${String(llamar)} por llamar`}</Chip> : c.affected.length === 0 ? 'Ninguna' : c.affected.length === 1 ? '1 afectada' : `${String(c.affected.length)} afectadas`,
                            estado: <Chip estado={chip}>{texto}</Chip>,
                        },
                    };
                })}
            />
            {creando && (
                <NuevoCierre
                    ops={ops}
                    negocio={negocio}
                    movil={movil}
                    contexto={contexto}
                    clientes={clientes}
                    onCerrar={(hecho) => {
                        setCerradoEn(nuevo);
                        if (hecho) {
                            avisar({ tipo: 'ok', texto: 'Cierre hecho. Avisamos a los clientes recolocados.' });
                            recargar();
                        }
                    }}
                />
            )}
            {abierto !== null && (
                <FichaCierre
                    ops={ops}
                    negocio={negocio}
                    movil={movil}
                    contexto={contexto}
                    cierre={abierto}
                    gestiona={gestiona}
                    clientes={clientes}
                    navegar={navegar}
                    onCambio={recargar}
                    onCerrar={() => { onAbrir(null); }}
                />
            )}
        </>
    );
}

interface Borrador {
    modo: 'recursos' | 'zonas';
    elegidos: string[];
    desde: string;
    desdeHora: string;
    hasta: string;
    hastaHora: string;
    motivo: CierreNuevo['reason_kind'];
    nota: string;
    recolocar: string[];
}

function NuevoCierre({ ops, negocio, movil, contexto, clientes, onCerrar }: { ops: Operaciones; negocio: string; movil: boolean; contexto: ContextoReservas; clientes: Readonly<Record<string, { nombre: string }>>; onCerrar: (hecho: boolean) => void }) {
    const hoy = hoyEn(contexto.zona);
    const [b, setB] = useState<Borrador>({ modo: 'recursos', elegidos: [], desde: hoy, desdeHora: '00:00', hasta: hoy, hastaHora: '23:59', motivo: 'breakdown', nota: '', recolocar: [] });
    const [impacto, setImpacto] = useState<Cierre | null>(null);
    const [cambios, setCambios] = useState<Readonly<Record<string, string>>>({});
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fallo = (e: unknown, defecto: string) => { setError(avisoBreveDeError(e) ?? (e instanceof ErrorApi ? e.message : defecto)); };
    const revisar = () => {
        setOcupado(true);
        setError(null);
        const datos: CierreNuevo = {
            targets: b.elegidos.map((id) => (b.modo === 'zonas' ? { zone_id: id } : { resource_id: id })),
            starts_at: instanteLocal(b.desde, b.desdeHora, contexto.zona),
            ends_at: instanteLocal(b.hasta, b.hastaHora, contexto.zona),
            reason_kind: b.motivo,
            ...(b.nota.trim() === '' ? {} : { reason: b.nota.trim() }),
            ...(b.recolocar.length === 0 ? {} : { relocation_zone_ids: b.recolocar }),
        };
        ops.previewResourceClosure(negocio, datos)
            .then((c) => { setImpacto(c); setCambios({}); })
            .catch((e: unknown) => { fallo(e, 'No se ha podido revisar el impacto.'); })
            .finally(() => { setOcupado(false); });
    };
    const confirmar = () => {
        if (impacto === null) {
            return;
        }
        setOcupado(true);
        const aceptadas = Object.entries(cambios).map(([reservation_id, v]) => (v === LLAMAR ? { reservation_id, outcome: 'needs_call' as const } : { reservation_id, outcome: 'relocate' as const, resource_ids: [v] }));
        ops.applyResourceClosure(negocio, impacto.id, aceptadas.length === 0 ? {} : { accepted_proposals: aceptadas })
            .then(() => { onCerrar(true); })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 409) {
                    setImpacto(null);
                    setError('La revisión ha caducado o algo ha cambiado. Revisa el impacto de nuevo.');
                } else {
                    fallo(e, 'No se ha podido hacer el cierre.');
                }
            })
            .finally(() => { setOcupado(false); });
    };

    const elegir = (id: string, si: boolean) => { setB({ ...b, elegidos: si ? [...b.elegidos, id] : b.elegidos.filter((x) => x !== id) }); };
    const valido = b.elegidos.length > 0 && `${b.hasta}T${b.hastaHora}` > `${b.desde}T${b.desdeHora}`;
    const recursoTexto = (r: Recurso) => `${mayuscula(contexto.palabra)} ${r.code ?? ''} • ${String(r.current_terms.min_capacity)} a ${String(r.current_terms.max_capacity)} pers.`;

    if (impacto !== null) {
        const afectadas = impacto.affected;
        const salida = (a: ReservaAfectada) => cambios[a.reservation_id] ?? (a.outcome === 'needs_call' ? LLAMAR : a.proposed_resource_ids?.[0] ?? LLAMAR);
        const llamar = afectadas.filter((a) => salida(a) === LLAMAR).length;
        const zonasRecolocar = b.recolocar.length > 0 ? b.recolocar : [...new Set(b.modo === 'zonas' ? b.elegidos : b.elegidos.map((id) => contexto.recursos.find((r) => r.id === id)?.current_terms.zone_id ?? ''))];
        const opciones = (a: ReservaAfectada) => [
            ...contexto.recursos
                .filter((r) => zonasRecolocar.includes(r.current_terms.zone_id) && !b.elegidos.includes(r.id) && r.current_terms.max_capacity >= a.party_size)
                .map((r) => ({ valor: r.id, texto: `${mayuscula(contexto.palabra)} ${r.code ?? ''} • ${contexto.zonas.find((z) => z.id === r.current_terms.zone_id)?.name ?? ''}` })),
            { valor: LLAMAR, texto: 'Llamar al cliente' },
        ];
        return (
            <PanelLateral
                abierto
                movil={movil}
                onCerrar={() => { onCerrar(false); }}
                icono="calendar-x"
                eyebrow="Paso 2 de 2 • Revisar el impacto"
                titulo={queSeCierra(impacto, contexto)}
                subtitulo={`${cuando(impacto, contexto.zona)} • ${MOTIVO[b.motivo] ?? b.motivo}`}
                accion="Confirmar el cierre"
                accionIcono="calendar-x"
                ocupado={ocupado}
                ocupadoTexto="Cerrando…"
                onAccion={confirmar}
            >
                <div className="rv-sl-form">
                    <div className="rv-rc-cifras">
                        <div><span>Afectadas</span><strong>{afectadas.length}</strong><small>reservas futuras</small></div>
                        <div><span>Se recolocan</span><strong>{afectadas.length - llamar}</strong><small>y les avisamos</small></div>
                        <div><span>Hay que llamar</span><strong>{llamar}</strong><small>{llamar === 0 ? 'Nadie' : 'sin sitio'}</small></div>
                    </div>
                    {impacto.preview_expires_at != null && (
                        <p className="rv-rs-presupuesto"><span><Icono nombre="hourglass" />{` Revisión válida hasta las ${horaEn(impacto.preview_expires_at, contexto.zona)}. Todavía no se ha cerrado nada.`}</span></p>
                    )}
                    {error !== null && <p className="rv-sl-nota rv-rc-error" role="alert">{error}</p>}
                    <h3 className="rv-rs-subtitulo">{`Reservas afectadas (${String(afectadas.length)})`}</h3>
                    {afectadas.length === 0 ? <p className="rv-sl-nota">No hay reservas en ese tiempo: nadie se queda sin sitio.</p> : <p className="rv-sl-nota">Te proponemos dónde recolocar cada una. Cámbialo o déjala para llamar.</p>}
                    {afectadas.map((a) => (
                        <div key={a.reservation_id} className="rv-rc-afectada">
                            <span>
                                <strong>{`${a.customer_id == null ? 'Cliente' : clientes[a.customer_id]?.nombre ?? 'Cliente'} • ${a.local_time ?? horaEn(a.starts_at, contexto.zona)}`}</strong>
                                <small>{`${diaCorto(a.local_date ?? hoyEn(contexto.zona, new Date(a.starts_at)))} • ${String(a.party_size)} personas`}</small>
                            </span>
                            <Desplegable etiqueta="Recolocar en" valor={salida(a)} opciones={opciones(a)} onChange={(v) => { setCambios({ ...cambios, [a.reservation_id]: v }); }} />
                        </div>
                    ))}
                    <button type="button" className="rv-b rv-b-sec" onClick={() => { setImpacto(null); }}><Icono nombre="pencil" /><span>Cambiar los datos del cierre</span></button>
                </div>
            </PanelLateral>
        );
    }

    return (
        <PanelLateral
            abierto
            movil={movil}
            onCerrar={() => { onCerrar(false); }}
            icono="calendar-x"
            eyebrow="Paso 1 de 2 • Cierre"
            titulo="Cerrar recursos"
            subtitulo="Todavía no se cierra nada."
            accion="Revisar el impacto"
            accionIcono="list-checks"
            ocupado={ocupado}
            ocupadoTexto="Revisando…"
            inactiva={!valido}
            onAccion={revisar}
        >
            <div className="rv-sl-form">
                <h3 className="rv-rs-subtitulo">Qué cierras</h3>
                <Segmentos etiqueta="Qué cierras" movil={movil} items={[{ etiqueta: `${mayuscula(contexto.palabra)}s sueltos`, icono: 'layout-grid' }, { etiqueta: 'Zonas enteras', icono: 'layers' }]} valor={b.modo === 'zonas' ? 1 : 0} onCambio={(i) => { setB({ ...b, modo: i === 1 ? 'zonas' : 'recursos', elegidos: [] }); }} />
                {b.modo === 'zonas'
                    ? contexto.zonas.map((z: Zona) => <Interruptor key={z.id} etiqueta={z.name} valor={b.elegidos.includes(z.id)} onChange={(v) => { elegir(z.id, v); }} />)
                    : contexto.zonas.map((z) => {
                        const suyos = contexto.recursos.filter((r) => r.current_terms.zone_id === z.id);
                        return suyos.length === 0 ? null : (
                            <div key={z.id} className="rv-rc-grupo">
                                <strong>{z.name}</strong>
                                {suyos.map((r) => <Interruptor key={r.id} etiqueta={recursoTexto(r)} valor={b.elegidos.includes(r.id)} onChange={(v) => { elegir(r.id, v); }} />)}
                            </div>
                        );
                    })}
                <h3 className="rv-rs-subtitulo">Cuándo</h3>
                <div className="rv-rc-dos">
                    <Fecha etiqueta="Desde el día" valor={b.desde} onChange={(v) => { setB({ ...b, desde: v, hasta: b.hasta < v ? v : b.hasta }); }} />
                    <Campo etiqueta="A las (HH:MM)" valor={b.desdeHora} onChange={(v) => { setB({ ...b, desdeHora: v }); }} max={5} />
                </div>
                <div className="rv-rc-dos">
                    <Fecha etiqueta="Hasta el día" valor={b.hasta} min={b.desde} onChange={(v) => { setB({ ...b, hasta: v }); }} />
                    <Campo etiqueta="A las (HH:MM)" valor={b.hastaHora} onChange={(v) => { setB({ ...b, hastaHora: v }); }} max={5} />
                </div>
                <h3 className="rv-rs-subtitulo">Motivo</h3>
                <Desplegable etiqueta="Motivo" valor={b.motivo} opciones={Object.entries(MOTIVO).map(([valor, texto]) => ({ valor, texto }))} onChange={(v) => { setB({ ...b, motivo: v as CierreNuevo['reason_kind'] }); }} />
                <Campo etiqueta="Nota (opcional)" valor={b.nota} onChange={(v) => { setB({ ...b, nota: v }); }} max={140} />
                <h3 className="rv-rs-subtitulo">Dónde recolocar</h3>
                <p className="rv-sl-nota">Sin elegir ninguna, en la misma zona.</p>
                {contexto.zonas.map((z) => <Interruptor key={z.id} etiqueta={z.name} valor={b.recolocar.includes(z.id)} onChange={(v) => { setB({ ...b, recolocar: v ? [...b.recolocar, z.id] : b.recolocar.filter((x) => x !== z.id) }); }} />)}
                {error !== null && <p className="rv-sl-nota rv-rc-error" role="alert">{error}</p>}
            </div>
        </PanelLateral>
    );
}

function FichaCierre({ ops, negocio, movil, contexto, cierre, gestiona, clientes, navegar, onCambio, onCerrar }: {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    contexto: ContextoReservas;
    cierre: Cierre;
    gestiona: boolean;
    clientes: Readonly<Record<string, { nombre: string; telefono: string | null }>>;
    navegar: (ruta: string) => void;
    onCambio: () => void;
    onCerrar: () => void;
}) {
    const avisar = useAvisoBreve();
    const [reabrir, setReabrir] = useState(false);
    const [cancelar, setCancelar] = useState<ReservaAfectada | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [ahora] = useState(() => Date.now());
    const [texto, chip, fase] = estadoDe(cierre, ahora);
    const porLlamar = cierre.affected.filter((a) => a.outcome === 'needs_call');
    const resto = cierre.affected.filter((a) => a.outcome !== 'needs_call');
    const motivo = [MOTIVO[cierre.reason_kind ?? ''] ?? cierre.reason_kind ?? '', cierre.reason ?? ''].filter((x) => x !== '').join(' • ');
    const fallo = (e: unknown, defecto: string) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? (e instanceof ErrorApi ? e.message : defecto) }); };
    // G883: dónde quedó cada reserva al aplicar el cierre y quién lo cerró, tal como los da la API.
    const recolocadas = [...new Set(cierre.affected.flatMap((a) => a.relocated_resource_ids ?? []))];
    const zonasRecolocadas = [...new Set(recolocadas.map((id) => contexto.recursos.find((r) => r.id === id)?.current_terms.zone_id ?? ''))]
        .map((z) => contexto.zonas.find((x) => x.id === z)?.name ?? '')
        .filter((x) => x !== '');
    const loCerro = cierre.applied_by_name != null && cierre.applied_at != null
        ? `${cierre.applied_by_name} • ${diaCorto(hoyEn(contexto.zona, new Date(cierre.applied_at)))}, ${horaEn(cierre.applied_at, contexto.zona)}`
        : null;
    const linea = (a: ReservaAfectada) => {
        const c = a.customer_id == null ? undefined : clientes[a.customer_id];
        const nombres = (ids: readonly string[]) => ids.map((id) => `${mayuscula(contexto.palabra)} ${contexto.recursos.find((r) => r.id === id)?.code ?? ''}`).join(' + ');
        // G883, G994: lo recolocado dice de dónde venía y dónde quedó («T3 → recolocada en S9», `original_resource_ids` y
        // `relocated_resource_ids`); sin recolocar, solo el recurso de origen.
        const origen = a.original_resource_ids.length > 0 ? a.original_resource_ids : a.resource_ids;
        const recolocada = a.outcome === 'relocated' && (a.relocated_resource_ids ?? []).length > 0;
        const sitio = recolocada ? `${origen.length > 0 ? `${nombres(origen)} → ` : ''}recolocada en ${nombres(a.relocated_resource_ids ?? [])}` : nombres(origen);
        return [c?.telefono ?? null, `${diaCorto(a.local_date ?? hoyEn(contexto.zona, new Date(a.starts_at)))}, ${a.local_time ?? horaEn(a.starts_at, contexto.zona)}`, `${String(a.party_size)} personas`, sitio].filter((x) => x !== null && x !== '').join(' • ');
    };

    return (
        <PanelLateral
            abierto
            movil={movil}
            tamano="ficha"
            onCerrar={onCerrar}
            icono="calendar-x"
            eyebrow="Cierre"
            titulo={queSeCierra(cierre, contexto)}
            chip={texto}
            chipEstado={chip}
            {...(gestiona && cierre.status === 'applied' && fase === 'activos' ? { accion: 'Reabrir', accionIcono: 'refresh-cw' as const, onAccion: () => { setReabrir(true); } } : {})}
        >
            <div className="rv-sl-form">
                <dl className="rv-rs-presupuesto">
                    <div><dt>Qué se cierra</dt><dd>{queSeCierra(cierre, contexto)}</dd></div>
                    <div><dt>Cuándo</dt><dd>{cuando(cierre, contexto.zona)}</dd></div>
                    <div><dt>Motivo</dt><dd>{motivo || '—'}</dd></div>
                    {cierre.applied_at != null && <div><dt>Recolocar en</dt><dd>{zonasRecolocadas.length > 0 ? zonasRecolocadas.join(', ') : 'No se recoloca'}</dd></div>}
                    {loCerro !== null && <div><dt>Lo cerró</dt><dd>{loCerro}</dd></div>}
                </dl>
                <h3 className="rv-rs-subtitulo">{`Clientes a llamar (${String(porLlamar.length)})`}</h3>
                {porLlamar.length === 0 ? <p className="rv-sl-nota">Nadie: todas las reservas tenían sitio.</p> : <p className="rv-sl-nota">No había sitio. Llámales y cambia la hora o cancela con devolución íntegra.</p>}
                {porLlamar.map((a) => {
                    const c = a.customer_id == null ? undefined : clientes[a.customer_id];
                    return (
                        <div key={a.reservation_id} className="rv-rc-afectada">
                            <span><strong>{c?.nombre ?? 'Cliente'}</strong><small>{linea(a)}</small></span>
                            <span className="rv-rs-acciones">
                                <Chip estado="pending">Por llamar</Chip>
                                {c?.telefono != null && <a className="rv-b rv-b-sec" href={`tel:${c.telefono}`}><Icono nombre="phone" /><span>Llamar</span></a>}
                                {gestiona && <button type="button" className="rv-b rv-b-sec" onClick={() => { navegar(`/reservas?apartado=dia&reserva=${encodeURIComponent(a.reservation_id)}`); }}><Icono nombre="calendar-clock" /><span>Otra hora</span></button>}
                                {gestiona && <button type="button" className="rv-b rv-b-sec rv-b-rojo" onClick={() => { setCancelar(a); }}><Icono nombre="circle-x" /><span>Cancelar y devolver</span></button>}
                            </span>
                        </div>
                    );
                })}
                {resto.length > 0 && (
                    <>
                        <h3 className="rv-rs-subtitulo">{`Recolocadas y avisadas (${String(resto.length)})`}</h3>
                        {resto.map((a) => {
                            const [t, e] = SALIDA[a.outcome];
                            return (
                                <div key={a.reservation_id} className="rv-rc-afectada">
                                    <span><strong>{a.customer_id == null ? 'Cliente' : clientes[a.customer_id]?.nombre ?? 'Cliente'}</strong><small>{linea(a)}</small></span>
                                    <Chip estado={e}>{t}</Chip>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
            <Ventana
                abierta={reabrir}
                titulo="¿Reabrir?"
                texto="Se puede volver a reservar desde ahora. Lo que ya se recolocó se queda donde está."
                accion="Reabrir"
                accionIcono="refresh-cw"
                cancelar="Volver"
                ocupado={ocupado}
                onAccion={() => {
                    setOcupado(true);
                    ops.reopenResourceClosure(negocio, cierre.id)
                        .then(() => { avisar({ tipo: 'ok', texto: 'Reabierto.' }); setReabrir(false); onCambio(); })
                        .catch((e: unknown) => { fallo(e, 'No se ha podido reabrir.'); })
                        .finally(() => { setOcupado(false); });
                }}
                onCerrar={() => { setReabrir(false); }}
            />
            <Ventana
                abierta={cancelar !== null}
                titulo="¿Cancelar y devolver?"
                texto="Cancelamos la reserva, le devolvemos todo lo que pagó y se lo decimos."
                accion="Cancelar y devolver"
                accionIcono="circle-x"
                cancelar="Volver"
                ocupado={ocupado}
                onAccion={() => {
                    if (cancelar === null) {
                        return;
                    }
                    setOcupado(true);
                    ops.cancelReservation(negocio, cancelar.reservation_id, 'Cierre de recursos sin sitio para recolocar')
                        .then(() => { avisar({ tipo: 'ok', texto: 'Reserva cancelada. Le devolvemos lo que pagó.' }); setCancelar(null); onCambio(); })
                        .catch((e: unknown) => { fallo(e, 'No se ha podido cancelar.'); })
                        .finally(() => { setOcupado(false); });
                }}
                onCerrar={() => { setCancelar(null); }}
            />
        </PanelLateral>
    );
}
